// src/config/api.js
//
// SINGLE SOURCE OF TRUTH for every backend base URL + auth header used
// across the whole app. Every page (login.js, company_register.js,
// company_product.js, company_home.js, etc.) should import from HERE
// instead of hardcoding its own `const API_BASE_URL = "..."`.
//
// Why this file exists: the isf-api backend is ONE deployed app that
// hosts two independent route groups under two different prefixes —
//   /isfathena/...  -> isfathena_router   (session ping/login/logout)
//   /growthos/...   -> isfAthenaMarketing/main.py, mounted as a sub-app
//                       (auth/login, company-registrations, products,
//                       invoices, bills, dropdown-options, etc.)
// Hardcoding these separately in every page is exactly how
// company_product.js ended up still pointing at "http://localhost:8000"
// in production — this file makes that impossible to repeat.

// ── Root deployed backend ────────────────────────────────────────────
// One env var controls every page at once. Set REACT_APP_API_BASE_URL
// in your .env(.production) file / DigitalOcean static-site build
// settings once you're ready — until then this falls back to the
// current production URL for every page automatically.
const ROOT_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://dolphin-app-33jp4.ondigitalocean.app";
  //process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// ── The two route groups ─────────────────────────────────────────────
export const ISFATHENA_BASE = `${ROOT_BASE}/isfathena`;
export const GROWTHOS_BASE = `${ROOT_BASE}/growthos`;

// ── Auth header helper ───────────────────────────────────────────────
// Every GrowthOS-protected page (products, invoices, bills, team, etc.)
// needs this exact header shape. One implementation, used everywhere,
// so a future change (e.g. switching storage keys) is a one-line edit.
export function authHeaders() {
  const token = localStorage.getItem("growthos_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Fetch wrapper for the GrowthOS backend ───────────────────────────
// Wraps: base URL + auth header + JSON parsing + the {success,message}
// error-shape this backend always returns (see main.py's custom
// HTTPException handler). Using this everywhere means the "data.error
// vs data.message vs data.detail" mismatch bugs we already found in
// login.js and company_register.js can't happen again in a new file.
//
// On a 401, it also redirects to /login automatically — every
// GrowthOS-protected page needs this same behavior (see
// company_product.js's fetchProducts for the pattern this replaces).
export async function growthosFetch(path, options = {}) {
  const res = await fetch(`${GROWTHOS_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body && !(options.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    window.location.href = "/login";
    return { res, data: null };
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    // non-JSON response (rare) — caller decides what to do with res.ok
  }
  return { res, data };
}

// ── Fetch wrapper for the isfathena backend (session ping/login/logout) ──
export async function isfathenaFetch(path, options = {}) {
  const res = await fetch(`${ISFATHENA_BASE}${path}`, options);
  let data = null;
  try {
    data = await res.json();
  } catch {
    // some isfathena endpoints (e.g. session/logout via sendBeacon) don't return JSON
  }
  return { res, data };
}
